import { useScrollAnimation, useTypingAnimation } from '../hooks/useScrollAnimation';
import { ArrowRight, Calendar, FileText, ClipboardCheck } from 'lucide-react';

const processSteps = ['Connect', 'Ingest', 'Analyze', 'Strategize', 'Execute'];

export default function HeroSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });
  const typedProcess = useTypingAnimation(processSteps.join(' → '), isVisible, 60);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#05060B] pt-20"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.15)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-cyan-400 text-sm font-medium">Now accepting new clients</span>
          </div>

          <h1 
            className={`font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white mb-6 leading-tight transition-all duration-1000 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Amazon growth management,{' '}
            <span className="text-cyan-400 cyan-glow">powered by AI.</span>
          </h1>

          <p 
            className={`text-lg sm:text-xl text-dark-400 max-w-3xl mx-auto mb-8 leading-relaxed transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Full-service campaign and account management for Amazon sellers and vendors. 
            We combine human expertise with AI intelligence to drive measurable growth.
          </p>

          <div 
            className={`mb-10 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="text-cyan-400 font-mono text-sm tracking-wider">
              {typedProcess}
              <span className="animate-pulse">|</span>
            </span>
          </div>

          <div 
            className={`flex flex-col sm:flex-row gap-4 justify-center mb-16 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <a href="#contact" className="btn-primary inline-flex items-center justify-center gap-2 group">
              <Calendar className="w-4 h-4" />
              Book Strategy Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="#reporting" className="btn-secondary inline-flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" />
              See Sample Reporting
            </a>
            <a href="#contact" className="btn-secondary inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" />
              Request Audit
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#05060B] to-transparent pointer-events-none" />
    </section>
  );
}
